import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchers } from "../fetchers";
import type {
  UploadProfileAvatarParams,
  DeleteProfileAvatarParams,
  ImageInsert,
  ImageUpdate,
} from "../fetchers/images";

const STORAGE_KEY = "storage";

/**
 * Create a new image record in the database
 */
const useCreateImage = () => {
  return useMutation({
    mutationKey: [STORAGE_KEY, "create"],
    mutationFn: (data: ImageInsert) => fetchers.storage.createImage(data),
  });
};

/**
 * Fetch an image record by ID
 */
const useGetImage = (imageId: string | null) => {
  return useQuery({
    queryKey: [STORAGE_KEY, "image", imageId],
    queryFn: () => (imageId ? fetchers.storage.getImage(imageId) : undefined),
    enabled: !!imageId,
  });
};

/**
 * Update an image record
 */
const useUpdateImage = () => {
  return useMutation({
    mutationKey: [STORAGE_KEY, "update"],
    mutationFn: ({ imageId, data }: { imageId: string; data: ImageUpdate }) =>
      fetchers.storage.updateImage(imageId, data),
  });
};

/**
 * Delete an image record
 */
const useDeleteImage = () => {
  return useMutation({
    mutationKey: [STORAGE_KEY, "delete"],
    mutationFn: (imageId: string) => fetchers.storage.deleteImage(imageId),
  });
};

/**
 * Get a signed URL for uploading to S3
 */
const useGetUploadSignedUrl = () => {
  return useMutation({
    mutationKey: [STORAGE_KEY, "uploadUrl"],
    mutationFn: (imageId: string) => fetchers.storage.getUploadSignedUrl(imageId),
  });
};

/**
 * Get a signed URL for downloading from S3
 */
const useGetDownloadSignedUrl = () => {
  return useMutation({
    mutationKey: [STORAGE_KEY, "downloadUrl"],
    mutationFn: (imageId: string) => fetchers.storage.getDownloadSignedUrl(imageId),
  });
};

/**
 * Download a file from S3 using a signed URL
 */
const useDownloadFileFromS3 = () => {
  return useMutation({
    mutationKey: [STORAGE_KEY, "downloadS3"],
    mutationFn: (signedUrl: string) => fetchers.storage.downloadFileFromS3(signedUrl),
  });
};

/**
 * Upload profile avatar (original and compressed versions)
 */
const useUploadProfileAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [STORAGE_KEY, "uploadProfileAvatar"],
    mutationFn: (params: UploadProfileAvatarParams) => fetchers.storage.uploadProfileAvatar(params),
    onSuccess: (_, { userId }) => {
      // Invalidate user's profile images query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [STORAGE_KEY, "userProfileImages", userId],
      });
    },
  });
};

/**
 * Delete profile avatar
 */
const useDeleteProfileAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [STORAGE_KEY, "deleteProfileAvatar"],
    mutationFn: (params: DeleteProfileAvatarParams) => fetchers.storage.deleteProfileAvatar(params),
    onSuccess: (_, { userId }) => {
      // Invalidate user's profile images query
      queryClient.invalidateQueries({
        queryKey: [STORAGE_KEY, "userProfileImages", userId],
      });
      // Invalidate all profile avatar URL queries
      queryClient.invalidateQueries({
        queryKey: [STORAGE_KEY, "profileAvatarUrl"],
      });
    },
  });
};

/**
 * Get profile avatar URL by image ID
 */
const useGetProfileAvatarUrl = (imageId: string | null | undefined) => {
  return useQuery({
    queryKey: [STORAGE_KEY, "profileAvatarUrl", imageId],
    queryFn: async () => {
      if (!imageId) return null;
      return await fetchers.storage.getProfileAvatarUrl(imageId);
    },
    enabled: !!imageId,
  });
};

/**
 * Get all profile avatar images for a user
 */
const useGetUserProfileImages = (userId: string | null) => {
  return useQuery({
    queryKey: [STORAGE_KEY, "userProfileImages", userId],
    queryFn: () => (userId ? fetchers.storage.getUserProfileImages(userId) : undefined),
    enabled: !!userId,
  });
};

export const storageHooks = {
  useCreateImage,
  useGetImage,
  useUpdateImage,
  useDeleteImage,
  useGetUploadSignedUrl,
  useGetDownloadSignedUrl,
  useDownloadFileFromS3,
  useUploadProfileAvatar,
  useDeleteProfileAvatar,
  useGetProfileAvatarUrl,
  useGetUserProfileImages,
};
